import { useEffect, useState } from 'react';
import {
  createBrowserRouter,
  NavLink,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { canManageUsers } from '@jfh/intake-shared';
import { api, type Me } from './lib/api.js';
import { loadMe } from './lib/useMe.js';
import { navCount } from './inbox/views.js';
import { Icon } from './components/Icon.js';
import { Avatar } from './components/Primitives.js';
import { ToastProvider } from './components/Toast.js';
import { FormsList } from './pages/FormsList.js';
import { Builder } from './pages/Builder.js';
import { Users } from './pages/Users.js';
import { Inbox } from './pages/Inbox.js';
import { NeedsFixing } from './pages/NeedsFixing.js';
import { problems } from './pages/needsFixing.js';
import { Settings } from './pages/Settings.js';
import { AuditLog } from './pages/AuditLog.js';
import { inTeams, teamsDeepLinkPath } from './lib/teams.js';
import { PublicForms } from './pages/PublicForms.js';
import logoIconWhite from './assets/logo-icon-white.png';

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

type Counts = Partial<Record<string, number>>;

/**
 * The menu's count bubbles (F10 P2): applications waiting for this person or
 * needing fixing per list, and Needs fixing (iQ and Teams problems). Refreshed on every page change
 * and every two minutes; a list that fails to load just shows no bubble.
 */
function useNavCounts(me: Me | null, path: string): Counts {
  const [counts, setCounts] = useState<Counts>({});
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 120_000);
    return () => window.clearInterval(t);
  }, []);
  useEffect(() => {
    if (!me) return;
    let live = true;
    const set = (to: string, n: number) => live && setCounts((c) => (c[to] === n ? c : { ...c, [to]: n }));
    for (const [to, group] of [
      ['/customers', 'customers'],
      ['/suppliers', 'suppliers'],
      ['/leads', 'leads'],
    ] as const) {
      api
        .listSubmissions(group)
        .then((r) => set(to, navCount(r.submissions, me, group === 'leads')))
        .catch(() => undefined);
    }
    api
      .needsFixing()
      .then((r) => set('/needs-fixing', problems(r.submissions).length))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [me, path, tick]);
  return counts;
}

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Applications',
    items: [
      { to: '/customers', label: 'Customers', icon: 'building' },
      { to: '/suppliers', label: 'Suppliers', icon: 'inbox' },
      { to: '/leads', label: 'Leads', icon: 'flag' },
    ],
  },
  {
    label: 'Manage',
    items: [
      { to: '/forms', label: 'Forms', icon: 'forms' },
      { to: '/needs-fixing', label: 'Needs fixing', icon: 'db' },
      { to: '/settings', label: 'Settings', icon: 'settings' },
    ],
  },
];

const ROLE_WORDS: Record<string, string> = { root: 'Root admin', admin: 'Admin', staff: 'Staff' };

type StatusWord = 'Set up' | 'Not set up' | null;

/**
 * The sidebar's footer: whether iQ, Teams and email are set up (admins only —
 * the settings behind it are admin-only). "Set up" means the settings are
 * filled in; Settings → Connections tests iQ for real.
 */
function SystemStatus() {
  const [iq, setIq] = useState<StatusWord>(null);
  const [teams, setTeams] = useState<StatusWord>(null);
  const [email, setEmail] = useState<StatusWord>(null);
  useEffect(() => {
    api
      .getApiConnection()
      .then((c) => setIq(c.baseUrl && c.bearerTokenSet ? 'Set up' : 'Not set up'))
      .catch(() => undefined);
    api
      .getSettings()
      .then((r) => {
        const ch = (r.settings.teams_channels ?? {}) as Record<string, { flowUrl?: string; mode?: string; bot?: unknown }>;
        const any = Object.values(ch).some((c) => c.flowUrl || (c.mode === 'bot' && c.bot));
        setTeams(any ? 'Set up' : 'Not set up');
      })
      .catch(() => undefined);
    api
      .getEmailConnection()
      .then((c) => setEmail(c.apiKeySet ? 'Set up' : 'Not set up'))
      .catch(() => undefined);
  }, []);
  const rows: [string, string, StatusWord][] = [
    ['db', 'iQ', iq],
    ['teams', 'Teams', teams],
    ['mail', 'Email', email],
  ];
  return (
    <NavLink to="/settings?section=connections" style={{ textDecoration: 'none' }} title="Settings → Connections">
      <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.12)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map(([ic, label, status]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 11.5, color: 'var(--jfh-pistachio)' }}>
            <Icon name={ic} size={13} />
            <span style={{ flex: 1, color: 'rgba(255,255,255,0.86)', fontWeight: 600 }}>{label}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 99,
                  background: status === 'Set up' ? 'var(--jfh-fern-green)' : status ? 'var(--warning-500)' : 'rgba(255,255,255,0.3)',
                }}
              />
              {status ?? '…'}
            </span>
          </div>
        ))}
      </div>
    </NavLink>
  );
}

function Shell() {
  const [me, setMe] = useState<Me | null>(null);
  const location = useLocation();
  // The builder owns its own full-height layout, so the shell content area
  // must not pad/scroll around it.
  const isBuilder = /^\/forms\/[^/]+$/.test(location.pathname);

  useEffect(() => {
    void loadMe().then(setMe);
  }, []);
  const counts = useNavCounts(me, location.pathname);

  // Opened from a Teams Activity notification: go straight to that application.
  const navigate = useNavigate();
  useEffect(() => {
    void teamsDeepLinkPath().then((path) => path && navigate(path, { replace: true }));
  }, [navigate]);

  // Show the Share forms, Audit log and Users nav items only to admins/root.
  const groups = NAV_GROUPS.map((g) =>
    g.label === 'Manage' && me && canManageUsers(me.role)
      ? {
          ...g,
          items: [
            ...g.items,
            { to: '/public', label: 'Share forms', icon: 'external' },
            { to: '/audit', label: 'Audit log', icon: 'audit' },
            { to: '/users', label: 'Users', icon: 'user' },
          ],
        }
      : g,
  );

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <aside
        className="jfh-side"
        style={{
          width: 'var(--sidebar-w)',
          flex: 'none',
          background: 'var(--jfh-mountain-green)',
          borderRight: 'none',
          display: 'flex',
          flexDirection: 'column',
          color: '#fff',
        }}
      >
        <a
          href="https://hub.jfhhorticultural.com"
          title="Back to the JFH Hub"
          className="jfh-hub"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '12px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.12)',
            color: 'rgba(255,255,255,0.74)',
            transition: 'color .14s, background .14s',
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
          </svg>
          Hub
        </a>
        <div
          style={{
            padding: '20px 20px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderBottom: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <img src={logoIconWhite} alt="JFH Horticultural Supplies" width={46} height={46} />
          <div>
            <div style={{ fontWeight: 900, fontSize: 17, lineHeight: 1.1, letterSpacing: '-0.01em', color: '#fff' }}>
              JFH Forms
            </div>
            <div style={{ fontSize: 12, color: 'var(--jfh-pistachio)', marginTop: 3 }}>
              Account &amp; lead portal
            </div>
          </div>
        </div>

        <nav style={{ padding: '18px 12px 10px', flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {groups.map((grp) => (
            <div key={grp.label} style={{ marginBottom: 14 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: 'var(--jfh-fern-green)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  padding: '0 11px 7px',
                }}
              >
                {grp.label}
              </div>
              {grp.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className="jfh-nav"
                  style={({ isActive }) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    width: '100%',
                    padding: '8px 11px',
                    borderRadius: 4,
                    textDecoration: 'none',
                    fontSize: 14,
                    fontWeight: isActive ? 800 : 600,
                    marginBottom: 2,
                    background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.78)',
                  })}
                >
                  <Icon name={n.icon} size={17} />
                  <span style={{ flex: 1 }}>{n.label}</span>
                  {Boolean(counts[n.to]) && (
                    <span
                      className="tnum"
                      aria-label={n.to === '/needs-fixing' ? `${counts[n.to]} problems` : `${counts[n.to]} to do`}
                      style={{
                        minWidth: 20,
                        height: 20,
                        padding: '0 6px',
                        borderRadius: 99,
                        fontSize: 11,
                        fontWeight: 800,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: n.to === '/needs-fixing' ? 'var(--st-declined)' : 'rgba(255,255,255,0.18)',
                        color: '#fff',
                      }}
                    >
                      {counts[n.to]}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {me && canManageUsers(me.role) && <SystemStatus />}
      </aside>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header
          style={{
            height: 56,
            flex: 'none',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '0 18px',
          }}
        >
          <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
            <Icon
              name="search"
              size={15}
              style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text-3)' }}
            />
            <input
              className="input"
              style={{ paddingLeft: 34, height: 36, background: 'var(--bg-app)' }}
              placeholder="Search applications, forms, applicants…"
            />
            <span className="kbd" style={{ position: 'absolute', right: 9, top: 8 }}>
              /
            </span>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ width: 1, height: 24, background: 'var(--border)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <Avatar name={me?.name || me?.email || 'JFH Staff'} size={30} />
            <div style={{ lineHeight: 1.2 }} title={me?.email}>
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>{me ? me.name || me.email : 'Not signed in'}</div>
              <div
                style={{
                  fontSize: 10.5,
                  color: 'var(--text-3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Icon name="shield" size={10} /> {me ? `${ROLE_WORDS[me.role] ?? 'Staff'} · ` : ''}
                {inTeams() ? 'signed in with Teams' : 'signed in with Cloudflare Access'}
              </div>
            </div>
          </div>
        </header>

        <main
          style={{
            flex: 1,
            minHeight: 0,
            overflow: isBuilder ? 'hidden' : 'auto',
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <Navigate to="/forms" replace /> },
      { path: 'forms', element: <FormsList /> },
      { path: 'forms/:id', element: <Builder /> },
      { path: 'users', element: <Users /> },
      { path: 'customers', element: <Inbox group="customers" /> },
      { path: 'suppliers', element: <Inbox group="suppliers" /> },
      { path: 'leads', element: <Inbox group="leads" /> },
      { path: 'needs-fixing', element: <NeedsFixing /> },
      { path: 'iq-failures', element: <Navigate to="/needs-fixing" replace /> },
      { path: 'public', element: <PublicForms /> },
      { path: 'audit', element: <AuditLog /> },
      { path: 'settings', element: <Settings /> },
      { path: '*', element: <Navigate to="/forms" replace /> },
    ],
  },
]);

export function App() {
  return (
    <ToastProvider>
      <RouterProvider router={router} />
    </ToastProvider>
  );
}
